import React from "react";
import { Box, Typography, Avatar, useTheme, alpha, Chip } from "@mui/material";
import {
  AutoAwesome as SparkleIcon,
  FlashOn as FlashIcon,
  Psychology as BrainIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { LANG } from "./constants";

const WelcomeScreen = ({ models, selectedModel, setSelectedModel, lang }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const basicModels = models
    .filter((m) => m.model_type === "basic")
    .slice(0, 3);
  const advancedModels = models
    .filter((m) => m.model_type === "advance")
    .slice(0, 3);

  const ModelGroup = ({ label, list, icon: Icon, color }) => {
    if (!list.length) return null;

    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <Icon sx={{ fontSize: 13, color }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: "text.disabled",
            }}
          >
            {label}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {list.map((m) => {
            const selected = selectedModel === m.key;

            return (
              <Box
                key={m.key}
                onClick={() => setSelectedModel(m.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.5,
                  py: 0.875,
                  borderRadius: 2.5,
                  cursor: "pointer",
                  minWidth: 0,
                  maxWidth: "100%",
                  border: `1.5px solid ${
                    selected
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.text.primary, isDark ? 0.03 : 0.02),
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: alpha(theme.palette.primary.main, 0.6),
                    bgcolor: alpha(theme.palette.primary.main, 0.07),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Avatar
                  src={m.icon_url}
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: "transparent",
                    flexShrink: 0,
                    "& img": { objectFit: "contain" },
                  }}
                />

                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: selected ? 700 : 500,
                    color: selected ? "primary.main" : "text.secondary",
                    lineHeight: 1,
                    minWidth: 0,
                  }}
                >
                  {m.short_name || m.name}
                </Typography>

                {selected && (
                  <CheckIcon
                    sx={{ fontSize: 13, color: "primary.main", flexShrink: 0 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2.5,
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 6 },
        pb: { xs: 6, sm: 8 },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: { xs: 64, sm: 72 },
          height: { xs: 64, sm: 72 },
          borderRadius: "22px",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.2,
          )}, ${alpha(
            theme.palette.secondary?.main || theme.palette.primary.dark,
            0.2,
          )})`,
          border: `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
          animation: "pulse 3s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": {
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
            "50%": {
              boxShadow: `0 8px 40px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
          },
        }}
      >
        <SparkleIcon
          sx={{ fontSize: { xs: 30, sm: 34 }, color: "primary.main" }}
        />
      </Box>

      <Box sx={{ textAlign: "center", maxWidth: "100%" }}>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${
              theme.palette.secondary?.main || theme.palette.primary.dark
            })`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 0.75,
            fontSize: { xs: "1.35rem", sm: "1.6rem" },
          }}
        >
          {lang?.startNewChat || LANG.startNewChat}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 380,
            mx: "auto",
            lineHeight: 1.65,
            fontSize: "0.875rem",
          }}
        >
          {lang?.welcomeDesc ||
            "Choose a model below and start chatting. Your conversations are saved automatically."}
        </Typography>
      </Box>

      {models.length > 0 && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: { xs: 1.5, sm: 2.5 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxShadow: isDark
              ? `0 4px 24px ${alpha(theme.palette.common.black, 0.2)}`
              : `0 4px 24px ${alpha(theme.palette.common.black, 0.05)}`,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              color="text.primary"
              sx={{ fontSize: "0.8375rem" }}
            >
              {lang?.selectModel || LANG.selectModel}
            </Typography>

            <Chip
              label={`${models.length} available`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.62rem",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 600,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>

          <ModelGroup
            label="Basic"
            list={basicModels}
            icon={FlashIcon}
            color={theme.palette.success.main}
          />

          <ModelGroup
            label="Advanced"
            list={advancedModels}
            icon={BrainIcon}
            color={theme.palette.warning.main}
          />
        </Box>
      )}
    </Box>
  );
};

export default WelcomeScreen;
